<template>
  <div class="product-hub">
    <!-- 页头 -->
    <div class="vtm-page-head">
      <div>
        <h1 class="vtm-page-head__title">{{ $t("products.title") }}</h1>
        <div class="vtm-page-head__sub">{{ $t("products.subtitle") }}</div>
      </div>
    </div>

    <!-- 视图切换（商品/素材库/分类/标签）——单一实例，紧贴页头下方 -->
    <div class="ph-tabs">
      <el-radio-group v-model="view" size="small">
        <el-radio-button label="products">{{ $t("products.tabProducts") }}</el-radio-button>
        <el-radio-button v-if="hubGrants.library" label="library">{{ $t("products.tabLibrary") }}</el-radio-button>
        <el-radio-button v-if="hubGrants.categories" label="categories">{{ $t("products.tabCategories") }}</el-radio-button>
        <el-radio-button v-if="hubGrants.tags" label="tags">{{ $t("products.tabTags") }}</el-radio-button>
      </el-radio-group>
    </div>

    <!-- KPI -->
    <section class="ph-kpis">
      <div @click="view = 'products'"><stat-card icon="el-icon-goods" :label="$t('products.kpiTotal')" :value="productPage.totalSize || 0"></stat-card></div>
      <div @click="view = 'categories'"><stat-card tone="info" icon="el-icon-collection-tag" :label="$t('products.kpiCategories')" :value="categoryOptions.length"></stat-card></div>
      <div @click="view = 'tags'"><stat-card tone="info" icon="el-icon-price-tag" :label="$t('products.kpiTags')" :value="tagOptions.length"></stat-card></div>
      <div @click="view = 'library'"><stat-card tone="ok" icon="el-icon-files" :label="$t('products.kpiLibrary')" :value="libPage.totalSize || 0"></stat-card></div>
    </section>

    <!-- 商品 -->
    <list-page
      v-show="view === 'products'"
      :loading="productLoading"
      :error="productError"
      :empty="!productList.length"
      :empty-text="$t('products.emptyProducts')"
      :total="productPage.totalSize || 0"
      :page-index="productPage.pageIndex"
      :page-size="productPage.pageSize"
      :page-sizes="[20, 50, 100]"
      @page-change="loadProducts"
      @size-change="onProductSize"
      @retry="loadProducts(productPage.pageIndex)"
    >
      <template #empty-action>
        <el-button type="primary" size="small" icon="el-icon-plus" v-hasPerm="['product:insert']" @click="openFinder">{{ $t("products.addProduct") }}</el-button>
      </template>
      <vtm-table
        table-id="products"
        :columns="productColumns"
        :data="sortedProducts"
        :loading="productLoading"
        :quick-search="false"
        max-height="600"
        :border="false"
        row-class-name="ph-row"
        @row-click="openDetail"
      >
        <template #toolbar-filters>
          <!-- 服务端字段检索：整库搜索（goods/page 支持 name/no） -->
          <el-input
            class="ph-search"
            size="small"
            clearable
            v-model="keyword"
            :placeholder="searchFieldLabel"
            @keyup.enter.native="submitSearch"
            @clear="submitSearch"
          >
            <el-select slot="prepend" v-model="searchField" class="ph-search-field" @change="onSearchFieldChange">
              <el-option :label="$t('products.colName')" value="name"></el-option>
              <el-option :label="$t('products.sku')" value="no"></el-option>
            </el-select>
            <el-button slot="append" icon="el-icon-search" @click="submitSearch"></el-button>
          </el-input>
          <el-select v-model="catFilter" size="small" clearable filterable :placeholder="$t('products.allCategories')" class="ph-filter" @change="submitSearch">
            <el-option v-for="c in categoryOptions" :key="c.id" :label="c.name" :value="c.id"></el-option>
          </el-select>
          <el-select v-model="sortKey" size="small" class="ph-filter" prefix-icon="el-icon-sort">
            <el-option v-for="o in sortOptions" :key="o.value" :label="o.label" :value="o.value"></el-option>
          </el-select>
        </template>
        <template #toolbar-right>
          <el-button type="primary" size="small" icon="el-icon-plus" v-hasPerm="['product:insert']" @click="openFinder">{{ $t("products.addProduct") }}</el-button>
        </template>
        <template #url="scope">
          <el-image class="ph-thumb" :src="firstImg(scope.row.url)" fit="contain">
            <div slot="error" class="ph-thumb__ph"><i class="el-icon-goods"></i></div>
            <div slot="placeholder" class="ph-thumb__ph"><i class="el-icon-goods"></i></div>
          </el-image>
        </template>
        <template #name="scope">
          <span class="ph-name">{{ scope.row.name }}</span>
        </template>
        <template #goodsTags="scope">
          <el-tag v-for="(t, i) in (scope.row.goodsTags || [])" :key="i" size="mini" type="info" class="ph-tag">{{ t.name }}</el-tag>
        </template>
        <template #costPrice="scope">
          <span class="vtm-num">{{ $currency(scope.row.costPrice) }}</span>
        </template>
        <template #num="scope">
          <span class="vtm-num">{{ scope.row.num || 0 }}</span>
        </template>
        <template #append>
          <el-table-column width="44" align="center">
            <template slot-scope="scope">
              <i class="el-icon-arrow-right ph-chev" @click.stop="openDetail(scope.row)"></i>
            </template>
          </el-table-column>
        </template>
      </vtm-table>
    </list-page>

    <!-- 素材库 -->
    <list-page
      v-show="view === 'library'"
      :loading="libLoading"
      :empty="!libList.length"
      :empty-text="$t('products.emptyLibrary')"
      :total="libPage.totalSize || 0"
      :page-index="libPage.pageIndex"
      :page-size="libPage.pageSize"
      @page-change="loadLibrary"
      @size-change="onLibSize"
    >
      <template #empty-action>
        <el-button type="primary" size="small" icon="el-icon-plus" v-hasPerm="['product:library:insert']" @click="addToLibrary">{{ $t("products.addToLibrary") }}</el-button>
      </template>
      <div class="ph-library-table">
        <vtm-table
          table-id="product-library"
          :columns="libColumns"
          :data="libList"
          :loading="libLoading"
          :quick-search="false"
          max-height="600"
          :border="false"
        >
          <!-- 服务端检索整个素材库（原来的快速搜索只过滤当前页 10 条，大库里像是搜不到） -->
          <template #toolbar-filters>
            <el-input
              class="ph-search"
              size="small"
              clearable
              prefix-icon="el-icon-search"
              v-model="libKeyword"
              :placeholder="$t('products.finderSearch')"
              @keyup.enter.native="loadLibrary(1)"
              @clear="loadLibrary(1)"
            ></el-input>
          </template>
          <template #toolbar-right>
            <el-button type="primary" size="small" icon="el-icon-plus" v-hasPerm="['product:library:insert']" @click="addToLibrary">{{ $t("products.addToLibrary") }}</el-button>
          </template>
          <template #url="scope">
            <el-image class="ph-thumb" :src="firstImg(scope.row.url)" fit="contain">
              <div slot="error" class="ph-thumb__ph"><i class="el-icon-goods"></i></div>
              <div slot="placeholder" class="ph-thumb__ph"><i class="el-icon-goods"></i></div>
            </el-image>
          </template>
          <template #name="scope">
            <span class="ph-name">{{ scope.row.name }}</span>
          </template>
          <template #append>
            <el-table-column fixed="right" :label="$t('common.operate')" width="240" align="center">
              <template slot-scope="scope">
                <el-button size="mini" type="success" icon="el-icon-download" @click="useFromLibrary(scope.row)">{{ $t("products.useThis") }}</el-button>
                <el-button size="mini" icon="el-icon-edit" v-hasPerm="['product:library:insert']" @click="editLibrary(scope.row)">{{ $t("common.edit") }}</el-button>
                <el-button
                  v-if="scope.row.uId == uId || pId == 0"
                  size="mini"
                  type="danger"
                  icon="el-icon-delete"
                  v-hasPerm="['product:library:delete']"
                  @click="removeLibrary(scope.row)"
                >{{ $t("common.delete") }}</el-button>
              </template>
            </el-table-column>
          </template>
        </vtm-table>
      </div>
    </list-page>

    <!-- 分类 -->
    <list-page
      v-show="view === 'categories'"
      :loading="catLoading"
      :empty="!catList.length"
      :empty-text="$t('products.emptyCategories')"
      :total="catPage.totalSize || 0"
      :page-index="catPage.pageIndex"
      :page-size="catPage.pageSize"
      @page-change="loadCategories"
      @size-change="onCatSize"
    >
      <template #empty-action>
        <el-button type="primary" size="small" icon="el-icon-plus" v-hasPerm="['product:type:insert']" @click="addCategory">{{ $t("products.newCategory") }}</el-button>
      </template>
      <vtm-table table-id="product-categories" :columns="taxColumns" :data="catList" :loading="catLoading" :search-fields="['name']" max-height="560" :border="false">
        <template #toolbar-right>
          <el-button type="primary" size="small" icon="el-icon-plus" v-hasPerm="['product:type:insert']" @click="addCategory">{{ $t("products.newCategory") }}</el-button>
        </template>
        <template #createTime="scope"><span class="vtm-num">{{ $formatDate(scope.row.createTime) }}</span></template>
        <template #append>
          <el-table-column fixed="right" :label="$t('common.operate')" width="160" align="center">
            <template slot-scope="scope">
              <el-button size="mini" icon="el-icon-edit" v-hasPerm="['product:type:update']" @click="editCategory(scope.row)">{{ $t("common.edit") }}</el-button>
              <el-button size="mini" type="danger" icon="el-icon-delete" v-hasPerm="['product:type:delete']" @click="delCategory(scope.row)">{{ $t("common.delete") }}</el-button>
            </template>
          </el-table-column>
        </template>
      </vtm-table>
    </list-page>

    <!-- 标签 -->
    <list-page
      v-show="view === 'tags'"
      :loading="tagLoading"
      :empty="!tagList.length"
      :empty-text="$t('products.emptyTags')"
      :total="tagPage.totalSize || 0"
      :page-index="tagPage.pageIndex"
      :page-size="tagPage.pageSize"
      @page-change="loadTags"
      @size-change="onTagSize"
    >
      <template #empty-action>
        <el-button type="primary" size="small" icon="el-icon-plus" v-hasPerm="['product:tag:insert']" @click="addTag">{{ $t("products.newTag") }}</el-button>
      </template>
      <vtm-table table-id="product-tags" :columns="taxColumns" :data="tagList" :loading="tagLoading" :search-fields="['name']" max-height="560" :border="false">
        <template #toolbar-right>
          <el-button type="primary" size="small" icon="el-icon-plus" v-hasPerm="['product:tag:insert']" @click="addTag">{{ $t("products.newTag") }}</el-button>
        </template>
        <template #createTime="scope"><span class="vtm-num">{{ $formatDate(scope.row.createTime) }}</span></template>
        <template #append>
          <el-table-column fixed="right" :label="$t('common.operate')" width="160" align="center">
            <template slot-scope="scope">
              <el-button size="mini" icon="el-icon-edit" v-hasPerm="['product:tag:update']" @click="editTag(scope.row)">{{ $t("common.edit") }}</el-button>
              <el-button size="mini" type="danger" icon="el-icon-delete" v-hasPerm="['product:tag:delete']" @click="delTag(scope.row)">{{ $t("common.delete") }}</el-button>
            </template>
          </el-table-column>
        </template>
      </vtm-table>
    </list-page>

    <!-- 新增漏斗 -->
    <product-finder-dialog :visible.sync="finderVisible" v-on:use="onFinderUse" v-on:create="onFinderCreate" />

    <!-- 商品详情飞出（就地编辑 + 自动保存） -->
    <product-detail-flyout
      :visible.sync="detailVisible"
      :product="activeProduct"
      :categories="categoryOptions"
      :tags="tagOptions"
      v-on:saved="loadProducts(productPage.pageIndex)"
      v-on:deleted="onProductDeleted"
      v-on:taxonomy-changed="loadTaxonomyOptions"
      v-on:edit-full="editFullProduct"
    />

    <!-- 创建 / 素材库新增·编辑（复用 AddProduct） -->
    <add-product
      :dialogProduct="dialogProduct"
      :product="product"
      :productTags="tagOptions"
      :fromMaterial="addFromMaterial"
      :fromLibrary="addFromLibrary"
      v-on:cancel="onAddCancel"
    />

    <!-- 分类 / 标签 新增·编辑 -->
    <add-product-type :dialogProductType="dialogCategory" :productType="editingCategory" v-on:cancel="onCategoryCancel" />
    <add-product-tag :dialogProductTag="dialogTag" :productTag="editingTag" v-on:cancel="onTagCancel" />
  </div>
</template>

<script>
  // 商品中心（Hub）：商品 / 素材库 / 分类 / 标签 四视图（页签与搜索·筛选·列设置·新增同处一行）。
  // 搜索优先的新增漏斗 + 就地编辑飞出。后端契约不变（goods/* · goodsType/* · goodsTag/* · goodsLib/*）。
  import ListPage from "_c/common/ListPage.vue";
  import { grantedLinks } from "@/utils/navSections";
  import VtmTable from "_c/common/VtmTable.vue";
  import StatCard from "_c/common/StatCard.vue";
  import productFinderDialog from "_c/product/ProductFinderDialog";
  import productDetailFlyout from "_c/product/ProductDetailFlyout";
  import addProduct from "_c/product/AddProduct";
  import addProductType from "_c/product/AddProductType";
  import addProductTag from "_c/product/AddProductTag";

  const blankProduct = () => ({
    costPrice: "",
    goodsTags: [],
    name: "",
    typeId: "",
    url: "",
    content: "",
    introduction: "",
    no: "",
    notes: "",
    id: ""
  });

  export default {
    name: "Product",
    components: { ListPage, VtmTable, StatCard, productFinderDialog, productDetailFlyout, addProduct, addProductType, addProductTag },
    data() {
      return {
        view: "products",
        pId: localStorage.getItem("pId") || -1,
        uId: localStorage.getItem("uId") || -1,
        // 商品
        productList: [],
        productLoading: false,
        productError: false,
        productPage: { pageSize: 50, pageIndex: 1, totalSize: 0 },
        catFilter: "",
        // 服务端整库检索（goods/page: name / no / typeName）
        keyword: "",
        searchField: "name",
        sortKey: "sales", // 默认按销量
        // 素材库
        libList: [],
        libLoading: false,
        libKeyword: "",
        libPage: { pageSize: 10, pageIndex: 1, totalSize: 0 },
        // 分类 / 标签（列表）
        catList: [],
        catLoading: false,
        catPage: { pageSize: 10, pageIndex: 1, totalSize: 0 },
        tagList: [],
        tagLoading: false,
        tagPage: { pageSize: 10, pageIndex: 1, totalSize: 0 },
        // 选项（用于筛选 / 飞出 / 表单）
        categoryOptions: [],
        tagOptions: [],
        // 弹层 / 飞出
        finderVisible: false,
        detailVisible: false,
        activeProduct: null,
        dialogProduct: false,
        product: blankProduct(),
        addFromMaterial: "0",
        addFromLibrary: false,
        addContext: "product",
        dialogCategory: false,
        editingCategory: {},
        dialogTag: false,
        editingTag: {}
      };
    },
    computed: {
      // 各分段对应的旧页面授权（子账号可能仅被授予部分页面）
      hubGrants() {
        const g = grantedLinks(this.$store.state.menuList);
        return {
          library: g.has("/product/product-library"),
          categories: g.has("/product/product-type"),
          tags: g.has("/product/product-tag")
        };
      },
      sortOptions() {
        return [
          { value: "sales", label: this.$t("products.sortSales") },
          { value: "nameAsc", label: this.$t("products.sortNameAsc") },
          { value: "category", label: this.$t("products.sortCategory") },
          { value: "priceAsc", label: this.$t("products.sortPriceAsc") },
          { value: "priceDesc", label: this.$t("products.sortPriceDesc") },
          { value: "newest", label: this.$t("products.sortNewest") }
        ];
      },
      // 检索与分类均已服务端过滤，这里直接透传
      filteredProducts() {
        return this.productList;
      },
      searchFieldLabel() {
        return this.searchField === "no" ? this.$t("products.sku") : this.$t("products.searchProducts");
      },
      // 客户端排序（后端 goods/page 无排序参数）；默认按销量倒序
      sortedProducts() {
        const num = v => Number(v) || 0;
        const str = v => String(v || "");
        // 忽略大小写（含重音等价）的字母序比较
        const ci = (a, b) => str(a).localeCompare(str(b), undefined, { sensitivity: "base", numeric: true });
        const cmp = {
          sales: (a, b) => num(b.num) - num(a.num),
          nameAsc: (a, b) => ci(a.name, b.name),
          category: (a, b) => ci(a.typeName, b.typeName) || ci(a.name, b.name),
          priceAsc: (a, b) => num(a.costPrice) - num(b.costPrice),
          priceDesc: (a, b) => num(b.costPrice) - num(a.costPrice),
          newest: (a, b) => str(b.createTime).localeCompare(str(a.createTime))
        };
        return [...this.filteredProducts].sort(cmp[this.sortKey] || cmp.sales);
      },
      productColumns() {
        return [
          { prop: "url", label: this.$t("products.colImage"), width: 72, align: "center", tooltip: false },
          { prop: "name", label: this.$t("products.colName"), minWidth: 200 },
          { prop: "typeName", label: this.$t("products.colCategory"), width: 150 },
          { prop: "goodsTags", label: this.$t("products.colTags"), minWidth: 160, tooltip: false },
          { prop: "costPrice", label: this.$t("products.colPrice"), width: 130, align: "right" },
          { prop: "num", label: this.$t("products.colSales"), width: 90, align: "right" },
          { prop: "createTime", label: this.$t("products.colCreated"), width: 160, align: "center", formatter: row => this.$formatDate(row.createTime) }
        ];
      },
      libColumns() {
        return [
          { prop: "url", label: this.$t("products.colImage"), width: 72, align: "center", tooltip: false },
          { prop: "name", label: this.$t("products.colName"), minWidth: 200 },
          { prop: "content", label: this.$t("products.description"), minWidth: 220 }
        ];
      },
      taxColumns() {
        return [
          { prop: "name", label: this.$t("productType.name"), minWidth: 180 },
          { prop: "notes", label: this.$t("productType.notes"), minWidth: 220 },
          { prop: "createTime", label: this.$t("productType.createTime"), width: 170, align: "center" }
        ];
      }
    },
    watch: {
      view(v) {
        if (v === "library" && !this.libList.length) {
          this.loadLibrary();
        }
        if (v === "categories" && !this.catList.length) {
          this.loadCategories();
        }
        if (v === "tags" && !this.tagList.length) {
          this.loadTags();
        }
      }
    },
    mounted() {
      this.loadTaxonomyOptions();
      this.loadProducts();
      this.loadLibraryCount();
    },
    methods: {
      firstImg(url) {
        if (!url) {
          return "";
        }
        return String(url).split(",")[0].trim();
      },
      // ---- 选项 ----
      loadTaxonomyOptions() {
        this.$api.request(this.$url.ProductType.list, this.$method.get).then(res => {
          if (res.code === 0) {
            this.categoryOptions = res.data || [];
          }
        });
        this.$api.request(this.$url.ProductTag.list, this.$method.get).then(res => {
          if (res.code === 0) {
            this.tagOptions = res.data || [];
          }
        });
      },
      // ---- 商品 ----
      submitSearch() {
        this.loadProducts(1);
      },
      onSearchFieldChange() {
        if (this.keyword) {
          this.loadProducts(1);
        }
      },
      loadProducts(pageIndex) {
        this.productLoading = true;
        this.productError = false;
        this.productPage.pageIndex = pageIndex ? pageIndex : 1;
        // 服务端检索参数：关键字按所选字段传递；分类以 typeName 传递
        const search = { name: "", no: "", typeName: "" };
        const kw = (this.keyword || "").trim();
        if (kw) {
          search[this.searchField] = kw;
        }
        if (this.catFilter) {
          const cat = this.categoryOptions.find(c => c.id === this.catFilter);
          if (cat) {
            search.typeName = cat.name;
          }
        }
        const obj = this.$cloud.objMerge(search, this.productPage);
        this.$api
          .request(this.$url.Product.page, this.$method.get, obj)
          .then(res => {
            if (res.code === 0 && res.data) {
              this.productList = res.data.records || [];
              this.productPage.totalSize = parseInt(res.data.total) || 0;
            }
            this.productLoading = false;
          })
          .catch(() => {
            this.productLoading = false;
            this.productError = true;
          });
      },
      onProductSize(size) {
        this.productPage.pageSize = size;
        this.loadProducts();
      },
      openDetail(row) {
        this.activeProduct = row;
        this.detailVisible = true;
      },
      // 完整编辑（含商品详情）：拷贝一份行数据，避免改动直接作用在列表行上
      editFullProduct(row) {
        this.product = Object.assign(blankProduct(), JSON.parse(JSON.stringify(row)));
        this.addFromMaterial = "0";
        this.addFromLibrary = true;
        this.addContext = "product";
        this.dialogProduct = true;
      },
      onProductDeleted() {
        this.loadProducts(this.productPage.pageIndex);
      },
      // ---- 新增漏斗 ----
      openFinder() {
        this.finderVisible = true;
      },
      onFinderUse(copy) {
        this.product = Object.assign(blankProduct(), copy, { id: "" });
        this.addFromMaterial = "0";
        this.addFromLibrary = true;
        this.addContext = "product";
        this.dialogProduct = true;
      },
      onFinderCreate() {
        this.product = blankProduct();
        this.addFromMaterial = "0";
        this.addFromLibrary = true;
        this.addContext = "product";
        this.dialogProduct = true;
      },
      onAddCancel(dialog, status) {
        this.dialogProduct = false;
        if (status) {
          if (this.addContext === "library") {
            this.loadLibrary(this.libPage.pageIndex);
          } else {
            this.loadProducts(this.productPage.pageIndex);
          }
        }
      },
      // ---- 素材库 ----
      loadLibrary(pageIndex) {
        this.libLoading = true;
        this.libPage.pageIndex = pageIndex ? pageIndex : 1;
        const query = this.$cloud.objMerge({}, this.libPage);
        if (this.libKeyword && this.libKeyword.trim()) {
          query.name = this.libKeyword.trim();
        }
        this.$api
          .request(this.$url.ProductLibrary.page, this.$method.get, query)
          .then(res => {
            if (res.code === 0 && res.data) {
              this.libList = res.data.records || [];
              this.libPage.totalSize = parseInt(res.data.total) || 0;
            }
            this.libLoading = false;
          })
          .catch(() => {
            this.libLoading = false;
          });
      },
      loadLibraryCount() {
        this.$api.request(this.$url.ProductLibrary.page, this.$method.get, { pageIndex: 1, pageSize: 1 }).then(res => {
          if (res.code === 0 && res.data) {
            this.libPage.totalSize = parseInt(res.data.total) || 0;
          }
        });
      },
      onLibSize(size) {
        this.libPage.pageSize = size;
        this.loadLibrary();
      },
      addToLibrary() {
        this.product = blankProduct();
        this.addFromMaterial = "1";
        this.addFromLibrary = true;
        this.addContext = "library";
        this.dialogProduct = true;
      },
      editLibrary(row) {
        this.product = Object.assign(blankProduct(), {
          id: row.id,
          name: row.name || "",
          url: row.url || "",
          content: row.content || "",
          introduction: row.introduction || "",
          notes: row.notes || ""
        });
        this.addFromMaterial = "1";
        this.addFromLibrary = true;
        this.addContext = "library";
        this.dialogProduct = true;
      },
      useFromLibrary(row) {
        const copy = {};
        ["name", "url", "content", "introduction", "notes"].forEach(k => {
          copy[k] = row[k] !== undefined && row[k] !== null ? row[k] : "";
        });
        this.onFinderUse(copy);
      },
      removeLibrary(row) {
        this.$confirm(this.$t("product.delete"), this.$t("common.warning"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        }).then(() => {
          this.$api.request(this.$url.ProductLibrary.delete, this.$method.delete, { id: row.id }).then(res => {
            if (res.code === 0) {
              this.$notify({ title: this.$t("common.tip"), message: res.msg, type: "success" });
              this.loadLibrary(this.libPage.pageIndex);
            } else {
              this.$message.error(res.msg);
            }
          });
        });
      },
      // ---- 分类 ----
      loadCategories(pageIndex) {
        this.catLoading = true;
        this.catPage.pageIndex = pageIndex ? pageIndex : 1;
        this.$api
          .request(this.$url.ProductType.page, this.$method.get, this.$cloud.objMerge({}, this.catPage))
          .then(res => {
            if (res.code === 0 && res.data) {
              this.catList = res.data.records || [];
              this.catPage.totalSize = parseInt(res.data.total) || 0;
            }
            this.catLoading = false;
          })
          .catch(() => {
            this.catLoading = false;
          });
      },
      onCatSize(size) {
        this.catPage.pageSize = size;
        this.loadCategories();
      },
      addCategory() {
        this.editingCategory = {};
        this.dialogCategory = true;
      },
      editCategory(row) {
        this.editingCategory = Object.assign({}, row);
        this.dialogCategory = true;
      },
      onCategoryCancel(dialog, status) {
        this.dialogCategory = false;
        if (status) {
          this.loadCategories(this.catPage.pageIndex);
          this.loadTaxonomyOptions();
        }
      },
      delCategory(row) {
        this.$confirm(this.$t("product.delete"), this.$t("common.warning"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        }).then(() => {
          this.$api.request(this.$url.ProductType.delete, this.$method.delete, { id: row.id }).then(res => {
            if (res.code === 0) {
              this.$notify({ title: this.$t("common.tip"), message: res.msg, type: "success" });
              this.loadCategories(this.catPage.pageIndex);
              this.loadTaxonomyOptions();
            }
          });
        });
      },
      // ---- 标签 ----
      loadTags(pageIndex) {
        this.tagLoading = true;
        this.tagPage.pageIndex = pageIndex ? pageIndex : 1;
        this.$api
          .request(this.$url.ProductTag.page, this.$method.get, this.$cloud.objMerge({}, this.tagPage))
          .then(res => {
            if (res.code === 0 && res.data) {
              this.tagList = res.data.records || [];
              this.tagPage.totalSize = parseInt(res.data.total) || 0;
            }
            this.tagLoading = false;
          })
          .catch(() => {
            this.tagLoading = false;
          });
      },
      onTagSize(size) {
        this.tagPage.pageSize = size;
        this.loadTags();
      },
      addTag() {
        this.editingTag = {};
        this.dialogTag = true;
      },
      editTag(row) {
        this.editingTag = Object.assign({}, row);
        this.dialogTag = true;
      },
      onTagCancel(dialog, status) {
        this.dialogTag = false;
        if (status) {
          this.loadTags(this.tagPage.pageIndex);
          this.loadTaxonomyOptions();
        }
      },
      delTag(row) {
        this.$confirm(this.$t("product.delete"), this.$t("common.warning"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        }).then(() => {
          this.$api.request(this.$url.ProductTag.delete, this.$method.delete, { id: row.id }).then(res => {
            if (res.code === 0) {
              this.$notify({ title: this.$t("common.tip"), message: res.msg, type: "success" });
              this.loadTags(this.tagPage.pageIndex);
              this.loadTaxonomyOptions();
            }
          });
        });
      }
    }
  };
</script>

<style scoped lang="scss">
  .product-hub {
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  .ph-kpis {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    padding: 0 16px 14px;

    > div {
      cursor: pointer;
    }
  }

  .ph-tabs {
    padding: 10px 16px 14px;
  }

  // Master Library's quick-search was getting cut off at VtmTable's shared 240px default —
  // widen just this instance rather than the shared component (used by many other tables).
  .ph-library-table ::v-deep .vtm-table__search {
    width: 420px;
    flex: 0 1 420px;
  }

  .ph-filter {
    width: 180px;
  }
  .ph-search {
    width: 480px;
  }
  .ph-search-field {
    width: 110px;
  }
  .ph-search ::v-deep .el-input-group__prepend {
    background: var(--vtm-surface-2);
  }

  .ph-row {
    cursor: pointer;
  }

  .ph-thumb {
    width: 46px;
    height: 46px;
    border-radius: var(--vtm-r-sm);
    background: var(--vtm-well);
    overflow: hidden;
  }
  .ph-thumb__ph {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: var(--vtm-ink-3);
    font-size: 18px;
    background: var(--vtm-well);
  }

  .ph-name {
    font-weight: 600;
    color: var(--vtm-ink);
  }

  .ph-tag {
    margin: 1px 3px 1px 0;
  }

  .ph-chev {
    color: var(--vtm-ink-3);
    cursor: pointer;

    &:hover {
      color: var(--vtm-mint-ink);
    }
  }
</style>
